/**
 * 数字/金额/比率的展示格式。
 *
 * 契约规则（packages/contracts/src/metrics-calc.ts）：
 *   分母为 0 的派生指标返回 null，**不得**当成 0 渲染 —— 0 是「真实为零」，
 *   null 是「无法计算」，两者在经营决策里含义完全相反。
 * 因此所有格式化函数都提供 `*OrDash` 版本，null/undefined/非有限数一律渲染为「—」。
 *
 * 金额一律是整数分（Cents），格式化时才除以 100。
 */

/** 缺失值占位符。统一用长破折号，避免和 0 混淆。 */
export const DASH = "\u2014";

const currencyFormatters = new Map<string, Intl.NumberFormat>();

function currencyFormatter(currency: string, fractionDigits: number): Intl.NumberFormat {
  const key = `${currency}:${fractionDigits}`;
  const cached = currencyFormatters.get(key);
  if (cached !== undefined) return cached;
  const created = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
    currencyDisplay: "narrowSymbol",
  });
  currencyFormatters.set(key, created);
  return created;
}

export function isMissing(value: number | null | undefined): boolean {
  return value === null || value === undefined || !Number.isFinite(value);
}

/** 整数分 → 货币字符串，例：1234567 → "$12,345.67" */
export function formatCents(cents: number, currency = "USD", fractionDigits = 2): string {
  return currencyFormatter(currency, fractionDigits).format(cents / 100);
}

/** 整数分 → 货币字符串；缺失 → 「—」 */
export function formatCentsOrDash(
  cents: number | null | undefined,
  currency = "USD",
  fractionDigits = 2,
): string {
  if (cents === null || cents === undefined || !Number.isFinite(cents)) return DASH;
  return formatCents(cents, currency, fractionDigits);
}

/** 整数千分位，例：1234567 → "1,234,567" */
export function formatInt(value: number): string {
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(value);
}

export function formatIntOrDash(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return DASH;
  return formatInt(value);
}

/** 紧凑整数，例：1234567 → "1.2M"。用于概览卡片，不用于表格数值列（表格要精确）。 */
export function formatCompact(value: number): string {
  return new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(
    value,
  );
}

export function formatCompactOrDash(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return DASH;
  return formatCompact(value);
}

/** 千分位比率 → 百分比，例：12.5% 存为 125 → "12.50%" */
export function formatPermille(value: number | null | undefined, digits = 2): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return DASH;
  return `${(value / 10).toFixed(digits)}%`;
}

/**
 * **ppm（百万分比）→ 百分比**，例：25% 存为 `250000` → `"25.00%"`。
 *
 * 花费总览的占比单位就是 ppm（契约 `spend-overview.ts` 的 `SPEND_OVERVIEW_PPM_SCALE`）。
 * `null` / 非有限数 → 「—」：**分母 0 或缺失时绝不显示 0%**（0% 是"确实没花钱"的结论）。
 */
export function formatPpm(value: number | null | undefined, digits = 2): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return DASH;
  return `${(value / 10_000).toFixed(digits)}%`;
}

/**
 * **向后兼容别名**：与 `formatRoas` 完全同实现（无 `×` 尾缀）。
 *
 * 2026-10-04 第十一批把「所有 ROAS 后边的 x 不需要」落到全站后，仓库内的生产调用点
 * 已全部改用 `formatRoas`；保留这个名字只是为了不破坏既有导出，**新代码请直接用
 * `formatRoas`**，全站不要再出现第二套比率格式化。
 * `null` / `undefined` / 非有限数仍然是「—」（不是 0）。
 */
export function formatRatio(value: number | null | undefined, digits = 2): string {
  return formatRoas(value, digits);
}

/**
 * **比率（ROAS 类无量纲倍数）的唯一实现**：`3.4211` → `"3.42"`，**无 `×` 尾缀**
 * （2026-10-04 第七批 · 用户第 2 条：「所有 ROAS 后边的 x 不需要」；
 * 第十一批把范围从广告管理域扩到全站 —— 概览页的 MER / 各 ROAS / 归因膨胀、
 * 行列表与行详情面板的 ROAS 列、整店历史表，全部走这里）。
 *
 * `formatRatio` 现在只是本函数的别名（见上），因此全站任何一处比率展示都不会再带 `×`。
 * `null` 仍然是「—」（不是 0）。
 *
 * ⚠️ **带单位的比率不要走这里**：转化率 / 点击率走 `formatPermille`（输出 `%`），
 * 变化率走 `formatDeltaPercent`（输出带符号的 `%`）。
 */
export function formatRoas(value: number | null | undefined, digits = 2): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return DASH;
  return value.toFixed(digits);
}

/** 带符号的百分比变化，例：0.123 → "+12.3%" */
export function formatDeltaPercent(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return DASH;
  const sign = value > 0 ? "+" : "";
  return `${sign}${(value * 100).toFixed(digits)}%`;
}

/** 固定小数位；缺失 → 「—」 */
export function formatFixed(value: number | null | undefined, digits = 2): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return DASH;
  return value.toFixed(digits);
}

/** 原始日期串直接展示（契约里日期就是 `YYYY-MM-DD` 业务日，不做时区转换） */
export function formatBusinessDate(value: string): string {
  return value;
}
